import React from 'react';
import App from './app/index';
import reportWebVitals from './reportWebVitals';
import axios from 'axios';
import PATH, {
  noRedirectToLoginPathnames,
} from 'crono-fe-common/constants/path';
import { createBrowserHistory } from 'history';
import { matchPath } from 'react-router-dom';
import 'styles/global.scss';
import 'styles/tailwind.css';
import 'styles/reset.css';
import { ENDPOINTS } from 'config/endpoints';
import { createRoot } from 'react-dom/client';
import { FeConstants } from 'constants/FeConstants';
import { Quill } from 'react-quill';
import { cookieDomain, cookies } from 'constants/crono-cookie';
import * as Sentry from '@sentry/react';
import { isDev, isLocal } from 'utils/uiHelpers';
import Gleap from 'gleap';
import { captureException } from 'utils/exceptionUtils';
import { retryWithFreshCsrf, shouldRetryCsrf } from 'utils/clients/csrfRetry';
import { trackApiFailure } from 'utils/clients/apiFailureTracking';
import { buildLoginRedirectPath } from 'utils/loginRedirect';
import { withBasename } from 'utils/basename';

// make ts happy
declare global {
  interface Window {
    Quill: typeof Quill;
    AwsWafCaptcha: {
      renderCaptcha: (
        element: HTMLElement,
        options: {
          onSuccess: (token: string) => void;
          onLoad?: () => void;
          onError?: () => void;
          onPuzzleTimeout?: () => void;
          onPuzzleIncorrect?: () => void;
          onPuzzleCorrect?: () => void;
          apiKey: string;
        },
      ) => void;
    };
    AwsWafIntegration?: {
      getToken: () => Promise<string>;
    };
    gr: {
      (a: 'track', b: 'conversion', c: { email?: string; uid?: string }): void;
      (
        a: 'loadReferralWidget',
        b: {
          product_id: string;
          auth_token: string;
          user_details: {
            email?: string;
            first_name?: string;
            last_name?: string;
            company_id?: string;
            company_name?: string;
          };
        },
      ): void;
    };
    referralWidget: {
      show: () => void;
    };
    CustomerScoreTracker: {
      identify: (params: {
        customer: { id: string | number };
        contact: object;
      }) => void;
      resetIdentity: () => void;
      track: (event: string, properties?: object) => void;
    };
    Frill?: (
      mode: 'widget',
      opts: Frill.WidgetConfig,
    ) => Promise<Frill.WidgetAPI> & { destroy?: () => void };
  }
}

window.addEventListener('vite:preloadError', (event) => {
  window.location.reload();
});

export const clearOutLocalStorage = () => {
  localStorage.removeItem('ajs_group_id');
  localStorage.removeItem('ajs_user_id');
  localStorage.removeItem('ajs_user_traits');
  localStorage.removeItem('ajs_group_properties');
  localStorage.removeItem('ajs_anonymous_id');

  cookies.remove(FeConstants.cronoCsrfCookie, {
    domain: cookieDomain,
  });
  cookies.remove(FeConstants.cronoCsrfLocalCookie, {
    domain: cookieDomain,
  });
};

export const history = createBrowserHistory();

axios.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const status = error?.response?.status;

    if (status === 403 && shouldRetryCsrf(error)) {
      try {
        return await retryWithFreshCsrf(error);
      } catch {
        trackApiFailure(error);
        return Promise.reject(error);
      }
    }

    trackApiFailure(error);

    if (error.response && error.response.status >= 400) {
      captureException(error, {
        level: 'error',
      });
    }

    if (status === 401) {
      const returnUrl = window.location.pathname;
      if (
        !matchPath(returnUrl, PATH.LOGIN) ||
        error.config.url.includes(ENDPOINTS.auth.me)
      ) {
        clearOutLocalStorage();
        const isNoRedirect = noRedirectToLoginPathnames.some(
          (link) => link.toLowerCase() === returnUrl.toLowerCase(),
        );
        if (!isNoRedirect) {
          history.push(
            withBasename(
              buildLoginRedirectPath(returnUrl, window.location.search),
            ),
            {
              error: true,
            },
          );
        }
      }
    }

    return Promise.reject(error);
  },
);

if (!isLocal()) {
  Sentry.init({
    dsn: 'https://a8acf47880994c6446591dbdab6f6abc@o4509506991751168.ingest.de.sentry.io/4509507063775312',
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    // Tracing
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: [
      'localhost',
      /^https:\/\/app\.crono\.one/,
      /^https:\/\/dev.app\.crono\.one/,
    ],
    // Session Replay
    replaysSessionSampleRate: 0, // This sets the sample rate at 0%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    environment: isDev() ? 'staging' : 'production',
  });
}
if (!isLocal()) {
  Gleap.initialize('7LhcTLpbHUflyAElgvPEu20iZWN3YqjC');
  Gleap.setEnvironment(isDev() ? 'staging' : 'prod');
  // We hide the button by default, then conditionally show it if user is logged in
  Gleap.showFeedbackButton(false);
}
const container = document.getElementById('root');
const root = createRoot(container!);
root.render(
  <React.StrictMode>
    <App history={history} />
  </React.StrictMode>,
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals())
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
